import { useEffect, useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { AlertCircle } from "lucide-react";
import { config } from "src/config/environment";
import { DEV_CAPTCHA_BYPASS_TOKEN, TURNSTILE_ACTION } from "src/config/turnstile";
import { cn } from "src/lib/utils";
import { Button } from "src/components/ui/Button";

interface CaptchaGateProps {
  onVerified: (token: string) => void;
  onReset: () => void;
  /** Increment after a rejected start so Turnstile issues a fresh single-use token. */
  resetSignal?: number;
  className?: string;
}

export function CaptchaGate({ onVerified, onReset, resetSignal = 0, className }: CaptchaGateProps) {
  const siteKey = config.turnstile.siteKey.trim();
  const allowDevSkip = config.environment === "development" && !siteKey;
  const widgetRef = useRef<TurnstileInstance>(null);
  const onResetRef = useRef(onReset);
  const skipReset = useRef(true);

  const [widgetKey, setWidgetKey] = useState(0);
  const [passed, setPassed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [skipped, setSkipped] = useState(false);

  onResetRef.current = onReset;

  useEffect(() => {
    if (skipReset.current) {
      skipReset.current = false;
      return;
    }
    setPassed(false);
    setFailed(false);
    setSkipped(false);
    widgetRef.current?.reset();
    onResetRef.current();
  }, [resetSignal]);

  const retry = () => {
    setPassed(false);
    setFailed(false);
    setWidgetKey((key) => key + 1);
    onReset();
  };

  if (!siteKey) {
    if (!allowDevSkip) {
      return (
        <div className={cn("w-full", className)} role="alert">
          <div className="flex items-start gap-2 rounded-md bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>The human check is unavailable. Refresh the page, or try again later.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={cn("w-full", className)}>
        <div className="rounded-xl border border-rfpurple-200 bg-rfpurple-50 px-4 py-4">
          <p className="text-sm leading-relaxed text-rfpurple-800">
            The human check is off in this environment, so you can move on without it.
          </p>
          {skipped ? (
            <p className="mt-3 text-sm font-medium text-[#3e1168]">Check skipped. Start the quiz when you are ready.</p>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="mt-3 border-rfpurple-200 text-[#3e1168] hover:bg-white"
              onClick={() => {
                setSkipped(true);
                onVerified(DEV_CAPTCHA_BYPASS_TOKEN);
              }}
            >
              Skip this check
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <p className="mb-3 text-sm text-gray-600">Confirm this, then start the quiz.</p>
      <div className="max-w-full overflow-x-auto">
        <Turnstile
          key={widgetKey}
          ref={widgetRef}
          siteKey={siteKey}
          onSuccess={(token) => {
            setFailed(false);
            setPassed(true);
            onVerified(token);
          }}
          onError={() => {
            setPassed(false);
            setFailed(true);
            onReset();
          }}
          onExpire={() => {
            setPassed(false);
            onReset();
          }}
          onTimeout={() => {
            setPassed(false);
            onReset();
          }}
          onUnsupported={() => {
            setPassed(false);
            setFailed(true);
            onReset();
          }}
          scriptOptions={{
            onError: () => {
              setPassed(false);
              setFailed(true);
              onReset();
            },
          }}
          options={{
            action: TURNSTILE_ACTION,
            theme: "light",
            size: "normal",
            appearance: "always",
            retry: "auto",
            refreshExpired: "auto",
            language: "en",
          }}
        />
      </div>

      {passed && (
        <p className="mt-3 text-sm font-medium text-[#3e1168]" aria-live="polite">
          Check complete.
        </p>
      )}

      {failed && (
        <div className="mt-3" role="alert">
          <p className="text-sm text-red-700">The check did not load. Try it again.</p>
          <Button
            type="button"
            variant="outline"
            className="mt-3 border-rfpurple-200 text-[#3e1168] hover:bg-white"
            onClick={retry}
          >
            Try the check again
          </Button>
        </div>
      )}
    </div>
  );
}

export default CaptchaGate;
